import { inject, Injectable } from '@angular/core';
import { IntegratorSiteService } from '@wheere/api-site';
import { IntegratorTrackerService } from '@wheere/api-tracker';
import { IntegratorGatewayService } from '@wheere/api-gateway';
import { AuthStore } from '@wheere-front/shared-auth';
import { IntegratorTracker } from '@wheere/api-tracker';
import { catchError, forkJoin, from, map, Observable, of } from 'rxjs';
import { AdminScopeService } from '../services/admin-scope.service';
import { COMMAND_PALETTE_CONFIG, CommandResultKind } from './command-palette.config';

export interface CommandResult {
  kind: CommandResultKind;
  id: string;
  label: string;
  sublabel?: string;
}

/**
 * Backs the global command palette (see {@link CommandPaletteComponent}). Queries sites /
 * devices / gateways via the **org-scoped integrator endpoints** (tenancy enforced server-side),
 * so the palette can never surface an entity the user isn't allowed to open. Organizations are
 * matched client-side over {@link AdminScopeService}'s catalogue and only for platform users.
 * Each source is best-effort: a failure yields an empty group, never an error.
 *
 * <p>Types interrogés : ceux que l'app déclare ouvrables par l'utilisateur
 * ({@link COMMAND_PALETTE_CONFIG}, même règle que ses gardes de route) ; un type non permis n'est
 * pas même demandé au serveur. Appareils : source de l'app si elle en fournit une (intégrateur :
 * `kind=TRACKER`, aucun baromètre), sinon le client généré (inside, baromètres compris).
 */
@Injectable({ providedIn: 'root' })
export class CommandSearchService {
  #sites = inject(IntegratorSiteService);
  #trackers = inject(IntegratorTrackerService);
  #gateways = inject(IntegratorGatewayService);
  #adminScope = inject(AdminScopeService);
  #auth = inject(AuthStore);
  #config = inject(COMMAND_PALETTE_CONFIG);

  /** Minimum query length before hitting the backend. */
  private static readonly MIN_CHARS = 2;
  private static readonly PER_GROUP = 6;

  search(query: string): Observable<CommandResult[]> {
    const q = query.trim();
    if (q.length < CommandSearchService.MIN_CHARS) {
      return of([]);
    }
    const org = this.#adminScope.selectedOrgId() ?? undefined;
    const n = CommandSearchService.PER_GROUP;
    const allowed = new Set(this.#config.allowedKinds());
    const none = of<CommandResult[]>([]);

    const sites$ = !allowed.has('site')
      ? none
      : this.#sites.getIntegratorSites(org, undefined, q, 0, n).pipe(
          map(r =>
            (r.content ?? []).map(
              (s): CommandResult => ({
                kind: 'site',
                id: s.id ?? '',
                label: s.name || '—',
                sublabel: s.slug ?? undefined,
              })
            )
          ),
          catchError(() => none)
        );

    const devices$ = !allowed.has('device')
      ? none
      : this.#devices(q, org, n).pipe(
          map(list =>
            list.map(
              (t): CommandResult => ({
                kind: 'device',
                id: t.id ?? '',
                label: t.name || '—',
                sublabel: t.deviceId ?? undefined,
              })
            )
          ),
          catchError(() => none)
        );

    const gateways$ = !allowed.has('gateway')
      ? none
      : this.#gateways.getIntegratorGateways(undefined, org, q, undefined, 0, n).pipe(
          map(r =>
            (r.content ?? []).map(
              (g): CommandResult => ({
                kind: 'gateway',
                id: g.id ?? '',
                label: g.name || '—',
                sublabel: g.deviceId ?? undefined,
              })
            )
          ),
          catchError(() => none)
        );

    // Organizations: cross-org, so platform users only. Client-side over the cached catalogue.
    const orgs: CommandResult[] =
      allowed.has('org') && this.#auth.isPlatformUser()
        ? this.#adminScope
            .orgs()
            .filter(o => (o.name ?? '').toLowerCase().includes(q.toLowerCase()))
            .slice(0, n)
            .map((o): CommandResult => ({ kind: 'org', id: o.id ?? '', label: o.name || '—' }))
        : [];

    return forkJoin([sites$, devices$, gateways$]).pipe(
      map(([sites, devices, gateways]) => [...sites, ...devices, ...gateways, ...orgs].filter(r => r.id))
    );
  }

  /** Appareils : source de l'app si fournie, sinon `GET /integrator/trackers` (tous les `kind`). */
  #devices(search: string, organizationId: string | undefined, size: number): Observable<IntegratorTracker[]> {
    if (this.#config.searchDevices) {
      return from(this.#config.searchDevices({ search, organizationId, size }));
    }
    return this.#trackers
      .getIntegratorTrackers(search, undefined, undefined, organizationId, 0, size)
      .pipe(map(r => r.content ?? []));
  }
}
